// Menu.tsx (imports)
import { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useWorkspace } from "@/features/Workspaces/WorkspaceContext";
import {
  buildWorkspaceAdminPath,
  buildWorkspaceDashboardPath,
  parseWorkspaceContextFromPathname,
  normalizeWorkspaceGroupForPath,
} from "@/features/Workspaces/workspaceRoutes";

// ...

// Flatten allowed routes from the filtered menu data
function computeAllowedRoutes(
  filteredWorkbooks: Record<string, Record<string, unknown>>, // { groupName: { uuid: item } }
  filteredTables: Record<string, Record<string, unknown>>, // { SectionName: { tableKey: tableInfo } }
  sectionRouteOverrides: Record<string, string>,
  workspace: string,
  group: string,
) {
  const routes: string[] = [];

  // dashboards
  for (const dashboardGroup of Object.values(filteredWorkbooks)) {
    for (const viewId of Object.keys(dashboardGroup)) {
      routes.push(
        buildWorkspaceDashboardPath({
          group,
          workspace,
          uuid: viewId,
        }),
      );
    }
  }

  // admin
  for (const [sectionName, tables] of Object.entries(filteredTables)) {
    const clientCode =
      sectionRouteOverrides[sectionName] ?? sectionName.toLowerCase();
    for (const tableKey of Object.keys(tables)) {
      routes.push(
        buildWorkspaceAdminPath({
          group,
          workspace,
          path: `${clientCode}/${tableKey}`,
        }),
      );
    }
  }

  return routes;
}

// Ensure current path is within allowed routes; otherwise jump to the first one
export function useRedirectIfNotAllowed(
  filteredWorkbooks: Record<string, Record<string, unknown>>,
  filteredTables: Record<string, Record<string, unknown>>,
  sectionRouteOverrides: Record<string, string>,
) {
  const location = useLocation();
  const navigate = useNavigate();
  const { current } = useWorkspace();
  const routeContext = parseWorkspaceContextFromPathname(location.pathname);

  const workspace = routeContext?.workspace ?? current?.code ?? "";
  const group =
    routeContext?.group ?? normalizeWorkspaceGroupForPath(current?.group);

  const allowedRoutes = useMemo(
    () =>
      computeAllowedRoutes(
        filteredWorkbooks,
        filteredTables,
        sectionRouteOverrides,
        workspace,
        group,
      ),
    [
      filteredWorkbooks,
      filteredTables,
      sectionRouteOverrides,
      workspace,
      group,
    ],
  );

  useEffect(() => {
    if (!workspace) return;
    if (allowedRoutes.length === 0) return; // nothing to show; keep current page

    // Keep deep links inside the current workspace scope, even if the table/view
    // is not listed in the filtered sidebar payload.
    const adminBase = buildWorkspaceAdminPath({ group, workspace });
    const dashboardBase = buildWorkspaceDashboardPath({ group, workspace });
    if (
      location.pathname.startsWith(adminBase) ||
      location.pathname.startsWith(dashboardBase)
    ) {
      return;
    }

    // Find if current path matches a prefix of any allowed route
    const onSamePage = allowedRoutes.some((r) =>
      location.pathname.startsWith(r),
    );
    if (!onSamePage) {
      navigate(allowedRoutes[0], { replace: true }); // go to first available
    }
  }, [allowedRoutes, location.pathname, navigate]);
}
